import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { ButtonBase, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRequestsContext } from "../../../contexts/RequestsContext";
import { useNowEveryMinute } from "../../../hooks/useNowEveryMinute";
import { ALPHA, FONT_SIZE, GAP_TONE, STATUS } from "../../../theme/tokens";
import type { OpenShiftRequest, ReplaceShiftRequest } from "../../../utils/models";
import { countNurseResponses } from "../../../utils/utilFunctions";
import { isUrgentClaim } from "./gapsRequests";

/** What `RequestsContext` holds: open shifts and replacement requests in one list. */
type StaffRequest = OpenShiftRequest | ReplaceShiftRequest;

type Props = {
	onOpen: () => void;
};

/** An answered request starting soon, with the relative time the banner prints for it. */
type UrgentClaim = { request: StaffRequest; start: DateTime; when: string };

/**
 * The 48-hour band: something somebody has already answered, starting soon.
 *
 * The artifact carries both this and the count on the Approve-shift slot and leaves the choice open;
 * both ship. They are not redundant — the badge tells you a number exists, and the banner tells you
 * which shift and how soon, which is what decides whether you act now. It appears only when there is
 * an urgent decision, so the pinned chrome it costs is only spent on a day that needs it.
 *
 * Which requests qualify is `isUrgentClaim` — answered, and starting inside `URGENT_CLAIM_WINDOW_HOURS` — the
 * same test Open Shifts draws its 48h band with, so the banner and that list can never disagree about what is
 * urgent. "Soon" is measured from the shift's start, not from when the request was posted — and from a "now" that moves
 * on while the home stays open (`useNowEveryMinute`, as the lists use), so a claim enters the band when it comes within
 * 48 hours and leaves it once the shift has started, rather than when the requests happen to refresh.
 */
export const GapsClaimsBanner: FC<Props> = ({ onOpen }) => {
	const { t } = useTranslation();
	const { staffRequests } = useRequestsContext();
	const now = useNowEveryMinute();

	// Requests with a nurse response are the only ones a banner can ask you to decide.
	const urgent = useMemo<UrgentClaim[]>(
		() =>
			staffRequests
				.filter((request) => isUrgentClaim(request, now))
				.map((request) => ({ request, start: request.message.shift_start, when: request.message.shift_start.toRelative({ base: now }) }))
				// `isUrgentClaim` already drops an invalid start, the only case `toRelative` answers null for; the guard
				// narrows the type rather than deciding anything.
				.filter((claim): claim is UrgentClaim => claim.when !== null)
				.sort((a, b) => a.start.toMillis() - b.start.toMillis()),
		[staffRequests, now],
	);

	if (urgent.length === 0) return null;

	const [soonest] = urgent;
	const others = urgent.length - 1;

	return (
		<ButtonBase
			data-testid="gaps-claims-banner"
			onClick={onOpen}
			sx={{
				width: "100%",
				display: "flex",
				alignItems: "center",
				gap: 1.125,
				px: 1.625,
				py: 1.125,
				textAlign: "left",
				// The missing rows' critical family — a soft wash and a status hairline — so the two red surfaces
				// on this screen read as one signal rather than two different alarms.
				bgcolor: GAP_TONE.CRITICAL_BG,
				borderTop: `1px solid ${alpha(STATUS.ERROR.main, ALPHA.STATUS_BORDER)}`,
				borderBottom: `1px solid ${alpha(STATUS.ERROR.main, ALPHA.STATUS_BORDER)}`,
				color: STATUS.ERROR.main,
			}}
		>
			<WarningAmberIcon sx={{ fontSize: 16, flex: "none" }} />
			<Typography component="span" sx={{ flex: 1, minWidth: 0, fontSize: FONT_SIZE.CAPTION, fontWeight: 600 }}>
				{/* The responses to the soonest claim — not the number of requests, which is always one here — counted as the */}
				{/* desktop Requests screen and the card that lists them count them. */}
				{t("gaps.banner.summary", { when: soonest.when, count: countNurseResponses(soonest.request) })}
				{others > 0 && (
					<Typography component="span" sx={{ fontWeight: 400, opacity: 0.85, fontSize: FONT_SIZE.CAPTION }}>
						{` · ${t("gaps.banner.more", { count: others })}`}
					</Typography>
				)}
			</Typography>
			<ChevronRightIcon sx={{ fontSize: 16, flex: "none", opacity: 0.6 }} />
		</ButtonBase>
	);
};
